import axios from "axios";

export const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:5003/api"
    : "https://wcco-backend.onrender.com/api");

export const AUTH_LOGOUT_EVENT = "wcco:auth-logout";
export const AUTH_TOKEN_REFRESHED_EVENT = "wcco:auth-token-refreshed";

const authApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { "X-WCCO-Client": "web" },
});

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

let refreshPromise = null;

const emit = (eventName, detail) => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }
};

export const storeAccessToken = (token) => {
  if (!token) return;
  localStorage.setItem("token", token);
  emit(AUTH_TOKEN_REFRESHED_EVENT, { token });
};

export const clearLocalSession = (reason = "session_expired") => {
  localStorage.removeItem("token");
  emit(AUTH_LOGOUT_EVENT, { reason });
};

export const renewAccessToken = async () => {
  if (!refreshPromise) {
    refreshPromise = authApi
      .post("/auth/refresh")
      .then((response) => {
        const token = response.data?.token;
        if (!token) throw new Error("Refresh response did not include an access token");
        storeAccessToken(token);
        return token;
      })
      .catch((error) => {
        error.authSessionExpired = true;
        clearLocalSession("session_expired");
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

export const logoutSession = async () => {
  try {
    await authApi.post("/auth/logout");
  } finally {
    clearLocalSession("signed_out");
  }
};

export const getAccessTokenExpiration = (token) => {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const payload = JSON.parse(atob(paddedBase64));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch (error) {
    return null;
  }
};

const isWccoApiRequest = (config) => {
  const url = config?.url || "";
  return config?.baseURL === API_BASE_URL || url.startsWith(API_BASE_URL);
};

const isPublicAuthRequest = (config) => {
  const url = config?.url || "";
  return [
    "/auth/signin",
    "/auth/signup",
    "/auth/forgot-password",
    "/auth/reset-password",
    "/auth/verify",
    "/auth/refresh",
  ].some((path) => url.includes(path));
};

const addAccessToken = (config) => {
  if (isWccoApiRequest(config)) {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.withCredentials = true;
  }
  return config;
};

const handleUnauthorized = (requestClient) => async (error) => {
  const originalRequest = error.config;
  const isUnauthorized = error.response?.status === 401;
  const canRefresh =
    isUnauthorized &&
    originalRequest &&
    isWccoApiRequest(originalRequest) &&
    !isPublicAuthRequest(originalRequest) &&
    !originalRequest._retry &&
    !originalRequest.skipAuthRefresh;

  if (!canRefresh) return Promise.reject(error);

  originalRequest._retry = true;

  try {
    const token = await renewAccessToken();
    originalRequest.headers = originalRequest.headers || {};
    originalRequest.headers.Authorization = `Bearer ${token}`;
    return requestClient(originalRequest);
  } catch (refreshError) {
    return Promise.reject(refreshError);
  }
};

api.interceptors.request.use(addAccessToken, (error) => Promise.reject(error));
api.interceptors.response.use(
  (response) => response,
  handleUnauthorized(api)
);

// Some older screens still use Axios directly. Applying the same behavior to
// the default client keeps renewal and logout consistent while those calls are
// incrementally migrated to the shared instance.
axios.interceptors.request.use(addAccessToken, (error) => Promise.reject(error));
axios.interceptors.response.use(
  (response) => response,
  handleUnauthorized(axios)
);

export default api;
